import React from "react";
import { FaPaperclip } from "react-icons/fa";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "react-datepicker/dist/react-datepicker.css";

// Helper to group fields into pairs, preserving headings
function groupFields(fields) {
  const rows = [];
  let currentRow = [];
  fields.forEach((field) => {
    if (field.type === "heading") {
      if (currentRow.length) {
        rows.push(currentRow);
        currentRow = [];
      }
      rows.push([field]);
    } else {
      currentRow.push(field);
      if (currentRow.length === 2) {
        rows.push(currentRow);
        currentRow = [];
      }
    }
  });
  if (currentRow.length) rows.push(currentRow);
  return rows;
}

const CommonModal = ({
  visible,
  title = "Form",
  fields = [],
  onCancel,
  onSubmit,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  values,
  onChange,
  errors = {},
  requiredFields = [],
  imagePreview = null,
  disabled = false,
}) => {
  if (!visible) return null;

  const handleFieldChange = (name, value) => {
    onChange({
      ...values,
      [name]: value,
    });
  };

  const rows = groupFields(fields);

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered">
        <div className="modal-content w-100" style={{ borderRadius: "16px" }}>
          <div className="modal-header border-bottom">
            <h4 className="modal-title text-muted">{title}</h4>
            <button type="button" className="btn-close mt-1" onClick={onCancel}></button>
          </div>

          <div className="modal-body mt-0 pt-0">
            {rows.map((fieldsRow, idx) =>
              fieldsRow.length === 1 && fieldsRow[0].type === "heading" ? (
                <div key={`heading-${idx}`} className="mt-1 mb-1 pb-1">
                  <h5 className="fw-semibold pt-2 text-muted">{fieldsRow[0].label}</h5>
                </div>
              ) : (
                <div className="row" key={`row-${idx}`}>
                  {fieldsRow.map((field) => {
                    const isRequired = requiredFields.includes(field.name);
                    const fieldError = errors[field.name];
                    const value = values[field.name] ?? "";
                    const fieldDisabled = disabled || field.readOnly;

                    return (
                      <div className="col-md-6 mb-2 p-2" key={field.name}>
                        {field.label && (
                          <label className="form-label text-muted">
                            {field.label}{" "}
                            {isRequired && <span className="text-danger">*</span>}
                          </label>
                        )}

                        {/* Textarea */}
                        {field.type === "textarea" ? (
                          <textarea
                            className={`form-control ${fieldError ? "is-invalid" : ""}`}
                            placeholder={field.placeholder || ""}
                            value={value}
                              disabled={fieldDisabled}
                            onChange={(e) =>
                              handleFieldChange(field.name, e.target.value)
                            }
                            rows={field.rows || 3}
                          />
                        ) : /* Select / Multiselect */
                          field.type === "select" ? (
                            <Select
                              classNamePrefix="select"
                              className={fieldError ? "is-invalid" : ""}
                              isDisabled={fieldDisabled}
                              options={field.options || []}
                              isMulti={field.isMulti}
                              placeholder={
                                field.placeholder || `Select ${field.label || field.name}`
                              }
                              value={
                                field.isMulti
                                  ? (value || []).map((val) =>
                                    field.options.find((opt) => opt.value === val)
                                  )
                                  : field.options.find(
                                    (opt) => opt.value === value
                                  ) || null
                              }
                              onChange={(selected) =>
                                handleFieldChange(
                                  field.name,
                                  field.isMulti
                                    ? (selected || []).map((s) => s.value)
                                    : selected?.value || ""
                                )
                              }
                            />
                          ) : /* Radio */
                            field.type === "radio" ? (
                              <div className="d-flex gap-3 flex-wrap">
                                {field.options?.map((opt) => (
                                  <label
                                    key={opt.value || opt}
                                    className="form-check-label"
                                  >
                                    <input
                                      type="radio"
                                      name={field.name}
                                      value={opt.value || opt}
                                      checked={value === (opt.value || opt)}
                                      disabled={fieldDisabled}
                                      onChange={(e) =>
                                        handleFieldChange(field.name, e.target.value)
                                      }
                                      className="form-check-input me-1"
                                    />
                                    {opt.label || opt}
                                  </label>
                                ))}
                              </div>
                            ) : /* Checkbox */
                              field.type === "checkbox" ? (
                                <div className="form-check mt-2">
                                  <input
                                    type="checkbox"
                                    className={`form-check-input ${fieldError ? "is-invalid" : ""
                                      }`}
                                    id={field.name}
                                    checked={!!value}
                                    disabled={fieldDisabled}
                                    style={{ transform: "scale(1.2)" }}
                                    onChange={(e) =>
                                      handleFieldChange(field.name, e.target.checked)
                                    }
                                  />
                                  <label
                                    className="form-check-label ms-2"
                                    htmlFor={field.name}
                                  >
                                    {field.placeholder || field.label}
                                  </label>
                                </div>
                              ) : /* Phone Input */
                                field.type === "phone" ? (
                                  <PhoneInput
                                    country={"in"}
                                    value={value}
                                    onChange={(phone) =>
                                      handleFieldChange(field.name, phone)
                                    }
                                    inputProps={{
                                      name: field.name,
                                      required: isRequired,
                                      readOnly: fieldDisabled,
                                    }}
                                    containerStyle={{ width: "100%" }}
                                    inputStyle={{
                                      width: "100%",
                                      borderRadius: "0.375rem",
                                      border: "1px solid #d1d5db",
                                      padding: "10px 45px 10px 48px",
                                    }}
                                  />
                                ) : /* File Upload */
                                  field.type === "file" ? (
                                    <>
                                      {/* Multiple file upload support */}
                                      {field.multiple ? (
                                        <>
                                          <input
                                            type="file"
                                            multiple
                                            className={`form-control ${fieldError ? "is-invalid" : ""}`}
                                            accept={field.accept || "*"}
                                            disabled={fieldDisabled}
                                            onChange={e => {
                                              const files = Array.from(e.target.files);
                                              handleFieldChange(field.name, files);
                                            }}
                                          />
                                          {/* Preview for multiple files */}
                                          <div className="mt-2 d-flex flex-wrap gap-2">
                                            {Array.isArray(values[field.name]) && values[field.name].length > 0 && values[field.name].map((f, i) => {
                                              if (f instanceof File && f.type && f.type.startsWith("image/")) {
                                                return (
                                                  <div key={i} className="position-relative me-2 mb-2">
                                                    <img src={URL.createObjectURL(f)} alt={f.name} style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 6 }} />
                                                    <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0" style={{ fontSize: 10 }} onClick={() => {
                                                      const newFiles = values[field.name].filter((_, idx) => idx !== i);
                                                      handleFieldChange(field.name, newFiles);
                                                    }}>×</button>
                                                  </div>
                                                );
                                              } else if (f instanceof File) {
                                                return (
                                                  <div key={i} className="d-flex align-items-center me-2 mb-2">
                                                    <span style={{ fontSize: 13 }}><FaPaperclip /> {f.name}</span>
                                                    <button type="button" className="btn btn-sm btn-danger ms-2" style={{ fontSize: 10 }} onClick={() => {
                                                      const newFiles = values[field.name].filter((_, idx) => idx !== i);
                                                      handleFieldChange(field.name, newFiles);
                                                    }}>×</button>
                                                  </div>
                                                );
                                              }
                                              return null;
                                            })}
                                          </div>
                                        </>
                                      ) : (
                                        // Single file upload (existing logic)
                                        (imagePreview && field.name === "images") || values[field.name] ? (
                                          <div className="mb-2">
                                            <img
                                              src={
                                                imagePreview ||
                                                (typeof values[field.name] === "string"
                                                  ? values[field.name]
                                                  : values[field.name]
                                                    ? URL.createObjectURL(values[field.name])
                                                    : "")
                                              }
                                              alt="Preview"
                                              className="img-thumbnail"
                                              style={{
                                                maxWidth: "200px",
                                                height: "auto",
                                                maxHeight: "150px",
                                              }}
                                            />
                                            <div className="mt-1">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-danger me-2"
                                                onClick={() =>
                                                  handleFieldChange(field.name, null)
                                                }
                                              >
                                                Remove Image
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => {
                                                  const input =
                                                    document.createElement("input");
                                                  input.type = "file";
                                                  input.accept = field.accept || "image/*";
                                                  input.onchange = (e) => {
                                                    const file = e.target.files[0];
                                                    if (file)
                                                      handleFieldChange(field.name, file);
                                                  };
                                                  input.click();
                                                }}
                                              >
                                                Change Image
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <input
                                            type="file"
                                            className={`form-control ${fieldError ? "is-invalid" : ""}`}
                                            accept={field.accept || "image/*"}
                                            disabled={fieldDisabled}
                                            onChange={(e) =>
                                              handleFieldChange(
                                                field.name,
                                                e.target.files[0] || null
                                              )
                                            }
                                          />
                                        )
                                      )}
                                    </>
                                  ) : /* Default Input */
                                    (
                                      <input
                                        name={field.name}
                                        type={field.type || "text"}
                                        className={`form-control ${fieldError ? "is-invalid" : ""
                                          }`}
                                        placeholder={field.placeholder || ""}
                                        value={value}
                                        disabled={fieldDisabled}
                                        readOnly={field.readOnly}
                                        onChange={(e) =>
                                          handleFieldChange(field.name, e.target.value)
                                        }
                                      />
                                    )}

                        {fieldError && (
                          <div className="invalid-feedback d-block">
                            {fieldError}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={disabled}
            >
              {cancelLabel}
            </button>

            <button
              type="button"
              className="btn btn-primary"
              style={{ borderRadius: "6px" }}
              onClick={onSubmit}
              disabled={disabled}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonModal;
